export enum UserRole {
  USER_WITH_ACCOUNT = 'user_with_account',
  NO_TEST = 'no_test',
  GENETICS = 'genetics',
  COVID_NON_DEPENDENT_PROXY = 'covid_proxy',
  COVID_FULL_USER_OR_DEPENDENT = 'covid_full_user_or_dependent',
  CARE_FULL_USER_OR_DEPENDENT = 'care_full_user_or_dependent',
  ESSENTIAL_CARE_USER = 'essential_care_user',
  STI_TEST_USER = 'sti_test_user',
  COLLECTION_SITE_ADMIN = 'collection_site_admin',
  COVID_SITE_MANAGER = 'covid_site_manager',
  PROVIDER = 'provider',
  SUPPORT_STAFF = 'support_staff',
  COLOR_EMPLOYEE = 'color_employee',
  DASHBOARD_VIEWER = 'dashboard_viewer',
  ACCESS_MANAGER = 'access_manager',
  SUPPORT_ADMIN = 'support_admin',
  ELIGIBILITY_DATA_MANAGER = 'eligibility_data_manager',
  SAMPLE_REVIEWER = 'sample_reviewer',
  ORDERING_PHYSICIAN = 'ordering_physician',
  GENETIC_COUNSELOR = 'genetic_counselor',
  EXTERNAL_CLASSIFICATION_REVIEWER = 'external_classification_reviewer',
  INTERNAL_CLASSIFICATION_REVIEWER = 'internal_classification_reviewer',
  VARIANT_REVIEWER = 'variant_reviewer',
  VARIANT_REVIEWER_MANAGER = 'variant_reviewer_manager',
  GENETICS_BOARD_MEMBER = 'genetics_board_member',
  PATHOLOGIST = 'pathologist',
  FULFILLMENT_STAFF = 'fulfillment_staff',
  CONTACT_MANAGER = 'contact_manager',
  VACCINATION_PLATFORM_SITE_MANAGER = 'vaccination_platform_site_manager',
  VACCINATION_PLATFORM_STAFF_MANAGER = 'vaccination_platform_staff_manager',
  VACCINATION_PLATFORM_SUPER_ADMIN = 'vaccination_platform_super_admin',
  VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER = 'vaccination_platform_site_inventory_manager',
  VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER = 'vaccination_platform_bulk_inventory_manager',
  VACCINATION_PLATFORM_SITE_STAFF = 'vaccination_platform_site_staff',
  VACCINATION_PLATFORM_SCRIBE = 'vaccination_platform_scribe',
  VACCINATION_PLATFORM_VACCINATING_CLINICIAN = 'vaccination_platform_vaccinating_clinician',
  LAB_STAFF = 'lab_staff',
}

export const ALL_USER_ROLES: UserRole[] = [
  UserRole.NO_TEST,
  UserRole.GENETICS,
  UserRole.COVID_NON_DEPENDENT_PROXY,
  UserRole.CARE_FULL_USER_OR_DEPENDENT,
  UserRole.COVID_FULL_USER_OR_DEPENDENT,
  UserRole.ESSENTIAL_CARE_USER,
  UserRole.STI_TEST_USER,
  UserRole.COVID_SITE_MANAGER,
  UserRole.PROVIDER,
  UserRole.SUPPORT_STAFF,
  UserRole.COLOR_EMPLOYEE,
  UserRole.DASHBOARD_VIEWER,
  UserRole.ACCESS_MANAGER,
  UserRole.ELIGIBILITY_DATA_MANAGER,
  UserRole.COLLECTION_SITE_ADMIN,
  UserRole.FULFILLMENT_STAFF,
  UserRole.CONTACT_MANAGER,
  UserRole.VACCINATION_PLATFORM_SITE_MANAGER,
  UserRole.VACCINATION_PLATFORM_STAFF_MANAGER,
  UserRole.VACCINATION_PLATFORM_SUPER_ADMIN,
];

// These represent users which should be regarded as being able to use genetics.
// In short, *all* users may use the Genetics platform by purchasing a test.
// See `role.ts` and `portals.ts` for the intricacies on how that affects app switching.
export const GENETICS_USER_ROLES: UserRole[] = [UserRole.GENETICS, UserRole.NO_TEST];

export enum CollectionSiteRole {
  ONSITE_STAFF = 'sample_collection_admin',
  BATCH_SHIPMENT_ADMIN = 'batch_shipment_admin',
  SITE_SCHEDULER = 'site_scheduler',
  SITE_MANAGER = 'site_manager',
  STAFF_MANAGER = 'staff_manager',
  VACCINE_INVENTORY_MANAGER = 'vaccine_inventory_manager',
  VACCINE_CLINICIAN = 'vaccine_clinician',
  SCRIBE = 'scribe',
}

export enum CollectionSiteRoleCategory {
  GENERAL = 'general',
  APPOINTMENT_MANAGEMENT = 'appointment_management',
  INVENTORY_MANAGEMENT = 'inventory_management',
  ONSITE_PATIENT_MANAGEMENT = 'onsite_patient_management',
  BATCH_SHIPMENT = 'batch_shipment',
}

export enum UserPermission {
  PUT_SAMPLES_ON_HOLD = 'samples.put_sample_on_hold',
  VIEW_USERS = 'users.view_users',
  VIEW_PHI = 'users.view_phi',
  VIEW_APPOINTMENTS = 'counseling.view_appointments',
  VIEW_HEALTH_HISTORY = 'health_profiles.view_healthhistory',
  VIEW_COVID_REPORTS_QUEUE = 'results.can_view_covid_reports_queue',
  ACCESS_PATIENT_DATA = 'colorhub.access_patient_data',
  ACCESS_SKIN_CLINIC = 'colorhub.access_skin_clinic',
  EDIT_SKIN_CLINIC = 'colorhub.edit_skin_clinic',
  REVIEW_SKIN_CLINIC = 'colorhub.review_skin_clinic',
  VIEW_PATIENTS_HUB = 'colorhub.view_patients_hub',
  USE_GLOBAL_SEARCH = 'colorhub.use_global_search',
  VIEW_PATIENT_RECORD = 'colorhub.view_patient_record',
  EDIT_PATIENT_DEMOGRAPHICS = 'colorhub.edit_patient_demographics',
  EDIT_PATIENT_CONTACT_INFO = 'colorhub.edit_patient_contact_info',
  EDIT_PATIENT_INSURANCE = 'colorhub.edit_patient_insurance',
  ADD_PATIENT_NOTES = 'colorhub.add_patient_notes',
  EDIT_PATIENT_NOTES = 'colorhub.edit_patient_notes',
  ARCHIVE_PATIENT_NOTES = 'colorhub.archive_patient_notes',
  VIEW_PATIENT_TIMELINE = 'colorhub.view_patient_timeline',
  VIEW_PATIENT_GOALS = 'colorhub.view_patient_goals',
  EDIT_PATIENT_GOALS = 'colorhub.edit_patient_goals',
  ADD_TASK = 'colorhub.add_task',
  EDIT_TASK = 'colorhub.edit_task',
  ADD_TASK_COMMENTS = 'colorhub.add_task_comments',
  EDIT_TASK_COMMENTS = 'colorhub.edit_task_comments',
  ARCHIVE_TASK_COMMENTS = 'colorhub.archive_task_comments',
  ADD_RISK_ASSESSMENT = 'colorhub.add_risk_assessment',
  ADD_DOCUMENT = 'colorhub.add_document',
  DELETE_DOCUMENT = 'colorhub.delete_document',
  DOWNLOAD_DOCUMENT = 'colorhub.download_document',
  EDIT_DOCUMENT = 'colorhub.edit_document',
  FAX_DOCUMENT = 'colorhub.fax_document',
  SHARE_DOCUMENT = 'colorhub.share_document',
  VIEW_DOCUMENT = 'colorhub.view_document',
  COLORHUB_ADMIN = 'colorhub.admin_hub',
  VIEW_CONTRACT = 'colorhub.view_contract',
  EDIT_CONTRACT = 'colorhub.edit_contract',
  FULFILL_FULFILLMENT_ORDERS = 'colorhub.fulfill_fulfillment_orders',
  VIEW_FULFILLMENT_ORDERS = 'colorhub.view_fulfillment_orders',
  VIEW_INBOUND_DOCUMENT = 'colorhub.view_inbound_document',
  EDIT_INBOUND_DOCUMENT = 'colorhub.edit_inbound_document',
  VIEW_SMS_MESSAGES = 'colorhub.view_sms_messages',
  SEND_SMS_MESSAGES = 'colorhub.send_sms_messages',
  ACCESS_CANCER_COPILOT = 'colorhub.access_cancer_copilot',
  ACCESS_METRIPORT = 'colorhub.access_metriport',
  VIEW_ALL_HEALTHBOOK_CHATS = 'colorhub.view_all_chats_healthbook',
  EDIT_PATIENT_EXPERIENCE_CONTENT = 'colorhub.edit_patient_experience_content',
}

export enum ScopedRoleType {
  COVID_OVERVIEW_DASHBOARD_VIEWER = 'view_covid_overview_dashboard',
  COVID_PATIENTS_DASHBOARD_VIEWER = 'view_covid_patients_dashboard',
  VACCINE_OVERVIEW_DASHBOARD_VIEWER = 'view_vx_overview_dashboard',
  VACCINE_PATIENTS_DASHBOARD_VIEWER = 'view_vx_patients_dashboard',
  VACCINE_AUDIT_DASHBOARD_VIEWER = 'view_vx_audit_dashboard',
  CONTACT_MANAGER = 'contact_manager',
  USER_MANAGER = 'manage_users',
  API_KEY_MANAGER = 'api_key_manager',
  LAB_OVERVIEW_DASHBOARD_VIEWER = 'lab_overview_dashboard_viewer',
  LAB_PHI_DASHBOARD_VIEWER = 'lab_phi_dashboard_viewer',
  ELIGIBILITY_DATA_MANAGER = 'eligibility_data_manager',
  RESEARCH_OVERVIEW_DASHBOARD_VIEWER = 'research_overview_dashboard_viewer',
  RESEARCH_CRO_OVERVIEW_DASHBOARD_VIEWER = 'research_cro_overview_dashboard_viewer',
  RESEARCH_PATIENTS_DASHBOARD_VIEWER = 'research_patients_dashboard_viewer',
  ESSENTIAL_CARE_OVERVIEW_DASHBOARD_VIEWER = 'essential_care_overview_dashboard_viewer',
  ESSENTIAL_CARE_PATIENTS_DASHBOARD_VIEWER = 'essential_care_patients_dashboard_viewer',
  CONSULTATIONS_OVERVIEW_DASHBOARD_VIEWER = 'consultations_overview_dashboard_viewer',
  CONSULTATIONS_PATIENTS_DASHBOARD_VIEWER = 'consultations_patients_dashboard_viewer',
  STI_OVERVIEW_DASHBOARD_VIEWER = 'sti_overview_dashboard_viewer',
  STI_PATIENTS_DASHBOARD_VIEWER = 'sti_patients_dashboard_viewer',
  ONSITE_BATCH_SHIPMENT_ADMIN = 'onsite_batch_shipment_admin',
  VACCINATION_PLATFORM_SITE_MANAGER = 'vaccination_platform_site_manager',
  VACCINATION_PLATFORM_SITE_STAFF = 'vaccination_platform_site_staff',
  VACCINATION_PLATFORM_SCRIBE = 'vaccination_platform_scribe',
  VACCINATION_PLATFORM_VACCINATING_CLINICIAN = 'vaccination_platform_vaccinating_clinician',
  VACCINATION_PLATFORM_SITE_SCHEDULER = 'vaccination_platform_site_scheduler',
  VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER = 'vaccination_platform_site_inventory_manager',
  VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER = 'vaccination_platform_bulk_inventory_manager',
  VACCINATION_PLATFORM_STAFF_MANAGER = 'vaccination_platform_staff_manager',
  VACCINATION_PLATFORM_SUPER_ADMIN = 'vaccination_platform_super_admin',
  PROMISE_OVERVIEW_DASHBOARD_VIEWER = 'promise_overview_dashboard_viewer',
  PROMISE_PARTICIPANTS_DASHBOARD_VIEWER = 'promise_participants_dashboard_viewer',
}

export enum PermissionType {
  UNKNOWN = 'UNKNOWN',
  ALLOWED = 'ALLOWED',
  NOT_ALLOWED = 'NOT_ALLOWED',
}
